
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

/* -------------------------------------------------------------------------- */
/* Supabase client – reads env vars at build‑time (Vite)                       */
/* -------------------------------------------------------------------------- */
// (Supabase client is now imported from '../../lib/supabase')

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */
interface PhotoInteractionsProps {
  mediaId: string;
  className?: string;
}

/* -------------------------------------------------------------------------- */
interface CommentReactions {
  [key: string]: {
    count: number;
    users: string[];
  };
}

interface Comment {
  id: string;
  comment_text: string;
  user_email: string;
  created_at: string;
  user_profiles?: {
    first_name: string;
    last_name: string;
  };
  reactions?: CommentReactions;
  userReactions?: string[];
}

interface ReactionCounts {
  [key: string]: {
    count: number;
    users: string[];
  };
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */
export function PhotoInteractions({
  mediaId,
  className = '',
}: PhotoInteractionsProps) {
  const { user } = useAuth();

  const [reactions, setReactions] = useState<ReactionCounts>({});
  const [comments, setComments] = useState<Comment[]>([]);
  const [userReactions, setUserReactions] = useState<string[]>([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const reactionEmojis = {
    like: '👍',
    love: '❤️',
    laugh: '😂',
    wow: '😮',
    sad: '😢',
  };

  // Users permitted to delete any comment
  const adminUsers = [
    'andrew@driveninsurance.com',
    'carlos@driveninsurance.com',
    'pj@driveninsurance.com',
  ];
  const isAdmin = !!user?.email && adminUsers.includes(user.email.toLowerCase());

  /* ---------------------------------------------------------------------- */
  /* Helper – fetch auth headers with better error handling                 */
  /* ---------------------------------------------------------------------- */
  const getAuthHeaders = async () => {
    try {
      const {
        data: { session },
        error: sessionError
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Failed to get session');
      }

      if (!session?.access_token) {
        console.error('No valid session found');
        throw new Error('No valid session found - please log in again');
      }

      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      };
    } catch (err) {
      console.error('Failed to get auth headers:', err);
      throw err;
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Load all reactions / comments for the current media                     */
  /* ---------------------------------------------------------------------- */
  const loadInteractions = async () => {
    if (!mediaId || !user?.email) {
      console.log('Missing mediaId or user email');
      return;
    }

    try {
      setLoadError(false);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.log('No valid session found');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/photo-interactions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'get_interactions',
            mediaId,
            userEmail: user.email,
          }),
        }
      );

      if (!response.ok) {
        console.log(`Response not OK: ${response.status}`);
        setLoadError(true);
        return;
      }

      const data = await response.json();
      
      if (!data.success) {
        console.log('API returned error:', data.error);
        setLoadError(true);
        return;
      }

      setReactions(data.reactions ?? {});
      
      const validComments = (data.comments ?? []).filter(
        (c: any) =>
          c?.id && c?.comment_text && c?.user_email && c?.created_at
      );
      setComments(validComments);
      setUserReactions(data.userReactions ?? []);
    } catch (err) {
      console.log('Could not load interactions:', err);
      setLoadError(true);
    }
  };

  useEffect(() => {
    if (!mediaId || !user) {
      console.log('Skipping loadInteractions - missing mediaId or user');
      return;
    }

    let retryCount = 0;
    const maxRetries = 2;

    const loadWithRetry = async () => {
      try {
        await loadInteractions();
      } catch (error) {
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying loadInteractions (attempt ${retryCount}/${maxRetries})`);
          setTimeout(loadWithRetry, 1000 * retryCount);
        } else {
          setLoadError(true);
        }
      }
    };

    loadWithRetry();
  }, [mediaId, user]);

  /* ---------------------------------------------------------------------- */
  /* Toggle a reaction on the photo with better error handling             */
  /* ---------------------------------------------------------------------- */
  const toggleReaction = async (reactionType: string) => {
    if (!user) return;

    setLoading(true);
    try {
      const headers = await getAuthHeaders();

      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/photo-interactions`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'toggle_reaction',
            mediaId,
            reactionType,
          }),
        }
      );

      if (!resp.ok) {
        if (resp.status === 401) {
          throw new Error('Session expired - please refresh the page and log in again');
        }
        throw new Error(`Server responded with ${resp.status}`);
      }
      const data = await resp.json();

      if (!data.success) {
        throw new Error(data.error ?? 'Unknown error');
      }

      // Update UI optimistically based on response payload
      setReactions((prev) => ({
        ...prev,
        [reactionType]: {
          count: data.count,
          users: data.userReacted
            ? [...(prev[reactionType]?.users ?? []), user.email!]
            : (prev[reactionType]?.users ?? []).filter(
                (e) => e !== user.email
              ),
        },
      }));
      setUserReactions((prev) =>
        data.userReacted
          ? [...prev.filter((r) => r !== reactionType), reactionType]
          : prev.filter((r) => r !== reactionType)
      );
    } catch (err: any) {
      console.error('Failed to toggle reaction:', err);
      if (err.message.includes('Session expired')) {
        alert(err.message);
      } else {
        alert(`Reaction error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Toggle a reaction on a comment with better error handling             */
  /* ---------------------------------------------------------------------- */
  const toggleCommentReaction = async (
    commentId: string,
    reactionType: string
  ) => {
    if (!user) return;

    try {
      const headers = await getAuthHeaders();

      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/photo-interactions`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'toggle_comment_reaction',
            commentId,
            reactionType,
          }),
        }
      );

      if (!resp.ok) {
        if (resp.status === 401) {
          throw new Error('Session expired - please refresh the page and log in again');
        }
        throw new Error(`Server responded with ${resp.status}`);
      }
      const data = await resp.json();

      if (!data.success) {
        throw new Error(data.error ?? 'Unknown error');
      }

      // Update only the affected comment
      setComments((prev) =>
        prev.map((c) => {
          if (c.id !== commentId) return c;

          const updatedReactions = {
            ...c.reactions,
            [reactionType]: {
              count: data.count,
              users: data.userReacted
                ? [...(c.reactions?.[reactionType]?.users ?? []), user.email!]
                : (c.reactions?.[reactionType]?.users ?? []).filter(
                    (e) => e !== user.email
                  ),
            },
          };

          const updatedUserReactions = data.userReacted
            ? [...(c.userReactions ?? []).filter((r) => r !== reactionType), reactionType]
            : (c.userReactions ?? []).filter((r) => r !== reactionType);

          return {
            ...c,
            reactions: updatedReactions,
            userReactions: updatedUserReactions,
          };
        })
      );
    } catch (err: any) {
      console.error('Failed to toggle comment reaction:', err);
      if (err.message.includes('Session expired')) {
        alert(err.message);
      } else {
        alert(`Comment reaction error: ${err.message}`);
      }
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Add a new comment with better error handling                          */
  /* ---------------------------------------------------------------------- */
  const addComment = async () => {
    if (!user || !newComment.trim()) return;

    setLoading(true);
    try {
      const headers = await getAuthHeaders();

      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/photo-interactions`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'add_comment',
            mediaId,
            commentText: newComment.trim(),
          }),
        }
      );

      if (!resp.ok) {
        if (resp.status === 401) {
          throw new Error('Session expired - please refresh the page and log in again');
        }
        throw new Error(`Server responded with ${resp.status}`);
      }
      const data = await resp.json();

      if (!data.success || !data.comment) {
        throw new Error(data.error ?? 'Failed to add comment');
      }

      setComments((prev) => [...prev, data.comment]);
      setNewComment('');
    } catch (err: any) {
      console.error('Failed to add comment:', err);
      if (err.message.includes('Session expired')) {
        alert(err.message);
      } else {
        alert(`Add comment error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Delete a comment (owner or admin) with better error handling          */
  /* ---------------------------------------------------------------------- */
  const deleteComment = async (commentId: string) => {
    if (!user) return;

    const ok = window.confirm(
      'Delete this comment? This action cannot be undone.'
    );
    if (!ok) return;

    setLoading(true);
    try {
      const headers = await getAuthHeaders();

      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/photo-interactions`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            action: 'delete_comment',
            commentId,
          }),
        }
      );

      if (!resp.ok) {
        if (resp.status === 401) {
          throw new Error('Session expired - please refresh the page and log in again');
        }
        throw new Error(`Server responded with ${resp.status}`);
      }
      const data = await resp.json();

      if (!data.success) {
        throw new Error(data.error ?? 'Failed to delete comment');
      }

      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err: any) {
      console.error('Failed to delete comment:', err);
      if (err.message.includes('Session expired')) {
        alert(err.message);
      } else {
        alert(`Delete comment error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Helper: format user name                                               */
  /* ---------------------------------------------------------------------- */
  const formatUserName = (c: Comment) => {
    if (c.user_profiles?.first_name && c.user_profiles?.last_name) {
      return `${c.user_profiles.first_name} ${c.user_profiles.last_name}`;
    }
    return c.user_email?.split('@')[0] ?? 'Unknown';
  };

  /* ---------------------------------------------------------------------- */
  /* Helper: relative date formatting                                      */
  /* ---------------------------------------------------------------------- */
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    return date.toLocaleDateString();
  };

  /* ---------------------------------------------------------------------- */
  /* Render – if no user, show login prompt                                 */
  /* ---------------------------------------------------------------------- */
  if (!user) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <p className="text-gray-500 text-sm text-center">
          Please log in to interact with photos
        </p>
      </div>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Show error state if loading failed                                     */
  /* ---------------------------------------------------------------------- */
  if (loadError) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <p className="text-gray-500 text-sm text-center">
          Unable to load interactions. Please try refreshing the page.
        </p>
      </div>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Main UI                                                               */
  /* ---------------------------------------------------------------------- */
  return (
    <div className={`bg-white rounded-lg p-4 ${className}`}>
      {/* Photo reactions */}
      <div className="flex items-center gap-2 mb-3">
        {Object.entries(reactionEmojis).map(([type, emoji]) => (
          <button
            key={type}
            onClick={() => toggleReaction(type)}
            disabled={loading}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${
              userReactions.includes(type)
                ? 'bg-blue-100 text-blue-600 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-sm">{emoji}</span>
            {reactions[type]?.count > 0 && (
              <span className="font-medium text-xs">{reactions[type].count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Toggle comment list */}
      <button
        onClick={() => setShowComments((s) => !s)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm mb-3"
      >
        <i className="ri-chat-3-line"></i>
        <span>
          {comments.length}{' '}
          {comments.length === 1 ? 'comment' : 'comments'}
        </span>
        <i className={`ri-arrow-${showComments ? 'up' : 'down'}-s-line`}></i>
      </button>

      {/* Comments section */}
      {showComments && (
        <div className="space-y-3">
          {/* New comment input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && !loading && addComment()}
              disabled={loading}
            />
            <button
              onClick={addComment}
              disabled={loading || !newComment.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-send-plane-line"></i>
              )}
            </button>
          </div>

          {/* List of comments */}
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {comments.length > 0 ? (
              comments.map((c) => (
                <div key={c.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-gray-800">
                      {formatUserName(c)}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {formatDate(c.created_at)}
                      </span>
                      {(c.user_email === user.email || isAdmin) && (
                        <button
                          onClick={() => deleteComment(c.id)}
                          disabled={loading}
                          className="text-red-500 hover:text-red-700 text-xs p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                          title={
                            isAdmin && c.user_email !== user.email
                              ? 'Delete comment (Admin)'
                              : 'Delete your comment'
                          }
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{c.comment_text}</p>

                  {/* Reactions on the comment */}
                  <div className="flex items-center gap-1 flex-wrap">
                    {Object.entries(reactionEmojis).map(([type, emoji]) => {
                      const count = c.reactions?.[type]?.count ?? 0;
                      const reacted = c.userReactions?.includes(type) ?? false;
                      return (
                        <button
                          key={type}
                          onClick={() => toggleCommentReaction(c.id, type)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${
                            reacted
                              ? 'bg-blue-100 text-blue-600 border border-blue-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          <span className="text-sm">{emoji}</span>
                          {count > 0 && (
                            <span className="font-medium text-xs">{count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 text-sm py-4">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoInteractions;
